/**
 * Okta - underscores, locales
 * Okta emails - hyphens, languages
 * Digital - hyphens, languages
 * sfPlatform - underscores, languages
 * Trailhead - hyphens, languages
 * GlobalNav - hyphens, locales
 */
/**
 * @description the full list of digital supported Locales, in Okta's locale format, sorted with the primary locale first for each lang tag (ie. 'es_ES' for just 'es', or 'en_US' for 'en')
 */
export const SUPPORTED_LOCALES = [
    'da_DK',
    'de_DE',
    'en_US',
    'en_AP',
    'en_AU',
    'en_CA',
    'en_EU',
    'en_GB',
    'en_IN',
    'eo_US',
    'es_ES',
    'es_MX',
    'fr_FR',
    'fi_FI',
    'fr_CA',
    'it_IT',
    'ja_JP',
    'ko_KR',
    'nl_NL',
    'no_NO',
    'pt_BR',
    'ru_RU',
    'sv_SE',
    'th_TH',
    'zh_CN',
    'zh_TW',
];
/**
 * @description The only locale GlobalNav doesn't support is Russian
 * and it uses '-' instead of '_'
 */
export const GLOBAL_NAV_SUPPORTED_LOCALES = [
    'da-DK',
    'de-DE',
    'en-AP',
    'en-AU',
    'en-CA',
    'en-EU',
    'en-GB',
    'en-IN',
    'en-US',
    'eo-US',
    'es-ES',
    'es-MX',
    'fi-FI',
    'fr-CA',
    'fr-FR',
    'it-IT',
    'ja-JP',
    'ko-KR',
    'nl-NL',
    'no-NO',
    'pt-BR',
    'sv-SE',
    'th-TH',
    'zh-CN',
    'zh-TW',
];
export const DEFAULT_LOCALE = 'en_US';
export const DEFAULT_LANG = 'en';
/**
 * @description The list of language tags supported by TBID
 */
export const DIGITAL_SUPPORTED_LANGUAGES = [
    'da',
    'de',
    'en',
    'eo',
    'es-MX',
    'es',
    'fi',
    'fr',
    'it',
    'ja',
    'ko',
    'nl',
    'no',
    'pt-BR',
    'ru',
    'sv',
    'th',
    'zh-CN',
    'zh-TW',
];
/**
 * @description Mapping of Language tags to Label
 */
export const LANGUAGE_LABEL_MAP = {
    da: 'Dansk',
    de: 'Deutsch',
    en: 'English',
    eo: 'Esperanto',
    'es-MX': 'Español (LATAM)',
    es: 'Español (España)',
    fi: 'Suomi',
    fr: 'Français',
    it: 'Italiano',
    ja: '日本語',
    ko: '한국어',
    nl: 'Nederlands',
    no: 'Norsk',
    'pt-BR': 'Português (Brasil)',
    ru: 'Русский',
    sv: 'Svenska',
    th: 'ภาษาไทย',
    'zh-CN': '简体中文',
    'zh-TW': '繁體中文',
};
/**
 * @description Profile services
 */
export const SF_PLATFORM_SUPPORTED_LANGUAGES = [
    'en_US',
    'es',
    'de',
    'fr',
    'it',
    'ja',
    'ko',
    'nl_NL',
    'es_MX',
    'pt_BR',
    'zh_CN',
];
/**
 * @description List of Salesforce platform supported languages
 */
export const SF_PLATFORM_LANGUAGES_MAP = SF_PLATFORM_SUPPORTED_LANGUAGES.reduce((map, sfLocale) => {
    switch (sfLocale) {
        case 'en_US':
            return { ...map, en: sfLocale };
        case 'nl_NL':
            return { ...map, nl: sfLocale };
        case 'es_MX':
        case 'pt_BR':
        case 'zh_CN': {
            const key = sfLocale.replace('_', '-');
            return { ...map, [key]: sfLocale };
        }
        default:
            return { ...map, sfLocale };
    }
}, {});
/**
 * @description List of Trailhead supported languages
 */
export const TRAILHEAD_SUPPORTED_LANGUAGES = [
    'en',
    'es',
    'de',
    'fr',
    'it',
    'ja',
    'ko',
    'es-MX',
    'pt-BR',
    'zh-CN',
];
/**
 * @description List of languages supported in translating emails
 */
export const OKTA_EMAIL_SUPPORTED_LANGUAGES = [
    'da',
    'de',
    'en',
    'es',
    'fi',
    'fr',
    'it',
    'ja',
    'ko',
    'nb',
    'nl-NL',
    'pt-BR',
    'ru',
    'sv',
    'th',
    'zh-CN',
    'zh-TW',
];
/**
 * Map Supported locales -> Okta values
 */
export const OKTA_EMAIL_LANGUAGES_MAP = OKTA_EMAIL_SUPPORTED_LANGUAGES.reduce((map, oktaLocale) => {
    switch (oktaLocale) {
        case 'nb':
            // Norwegian language code in Okta is 'nb' instead of 'no'
            return { ...map, no: oktaLocale };
        case 'nl-NL':
            return { ...map, nl: 'nl-NL' };
        case 'es':
            // Okta doesn't support Mexican Spanish explicitly though we support it
            return { ...map, oktaLocale, 'es-MX': oktaLocale };
        default:
            return { ...map, oktaLocale };
    }
}, {});
/**
 * @description salesforce.com path segment that identifies the regional website. E.g.`uk` in https://www.salesforce.com/uk
 */
export const WWW_SFDC_REGIONS = [
    '',
    'au',
    'ap',
    'ca',
    'cn',
    'br',
    'de',
    'eu',
    'es',
    'fr',
    'fr-ca',
    'in',
    'it',
    'jp',
    'kr',
    'mx',
    'nl',
    'se',
    'th',
    'tw',
    'uk',
];
/**
 * @description Mapping of salesforce.com path segment that identifies the regional website (E.g.`uk` in https://www.salesforce.com/uk) by GlobalNav locale
 */
export const WWW_SFDC_REGION_BY_LOCALE = {
    'da-DK': 'eu',
    'de-DE': 'de',
    'en-AP': 'ap',
    'en-AU': 'au',
    'en-CA': 'ca',
    'en-EU': 'eu',
    'en-GB': 'uk',
    'en-IN': 'in',
    'en-US': '',
    'eo-US': '',
    'es-ES': 'es',
    'es-MX': 'mx',
    'fi-FI': 'eu',
    'fr-CA': 'fr-ca',
    'fr-FR': 'fr',
    'it-IT': 'it',
    'ja-JP': 'jp',
    'ko-KR': 'kr',
    'nl-NL': 'nl',
    'no-NO': 'eu',
    'pt-BR': 'br',
    'sv-SE': 'se',
    'th-TH': 'th',
    'zh-CN': 'cn',
    'zh-TW': 'tw',
};
function sourceLocaleFromBrowser() {
    if (typeof navigator === 'undefined') {
        throw new Error('Unable to get locale, not running in browser');
    }
    let language = navigator.language;
    if (!language) {
        language = navigator.languages[0];
    }
    return language;
}
function getSupportedLocale({ langTag, regionTag, }) {
    if (!regionTag) {
        // Unable to lookup locale without country, just use default for langTag
        // Find the first supported full locale for this language
        // NOTE: assumes langTag is supported by the list of locales
        return (SUPPORTED_LOCALES.find((supportedLocale) => {
            const [thisLocaleLang] = supportedLocale.split('_');
            return thisLocaleLang === langTag;
        }) ?? DEFAULT_LOCALE);
    }
    const desiredLocale = `${langTag}_${regionTag}`;
    let foundLocale = SUPPORTED_LOCALES.find((supportedLocale) => supportedLocale.toLowerCase() === desiredLocale);
    // Attempt to match on just the lang tag and get the default locale for that
    if (!foundLocale) {
        foundLocale = SUPPORTED_LOCALES.find((supportedLocale) => {
            const supportedLang = supportedLocale.split('_')[0];
            return supportedLang.toLowerCase() === langTag.toLowerCase();
        });
    }
    return foundLocale ?? DEFAULT_LOCALE;
}
/**
 * @param {string} [params.locale] - string to get locale from. Defaults to `navigator.language`
 * @param {string} [params.countryCode] - string to use as the country code for the locale
 * @param {LocaleFormat} [params.to=okta] - target format for the locale, defaults to 'okta'
 * @description Use to get a supported and properly formatted locale
 * @returns {SupportedLocale | GlobalNavSupportedLocale}
 */
export function getLocale({ locale, countryCode, to = 'okta', } = {}) {
    let rawLocale = locale;
    if (!rawLocale) {
        rawLocale = sourceLocaleFromBrowser();
    }
    // Parse out language and locale
    const regex = rawLocale.match(/^(?<lang>[a-z]{2})?(?:[_-])?(?<locale>[a-z]{2})?$/i);
    // Create a valid tag
    // https://www.loc.gov/standards/iso639-2/php/English_list.php
    // https://datatracker.ietf.org/doc/html/rfc5646
    const tag = [regex?.groups?.lang, regex?.groups?.locale?.toUpperCase()]
        .filter(Boolean)
        .join('_') || rawLocale;
    // Normalize to Okta-style locale
    const normalizedLocale = tag.toLowerCase();
    const [langTag, normalizedCountry] = normalizedLocale.split('_');
    let regionTag = countryCode?.toLowerCase();
    if (!regionTag) {
        regionTag = normalizedCountry;
    }
    const supportedLocale = getSupportedLocale({ langTag, regionTag });
    if (to === 'globalNav') {
        const globalNavLocale = langTag === 'ru' ? DEFAULT_LOCALE : supportedLocale;
        return globalNavLocale.replace('_', '-');
    }
    return supportedLocale;
}
function getSupportedLang({ locale, supportedLangs, }) {
    const mutableArr = [...supportedLangs]; // Simply to appease TS deities
    let foundLang = mutableArr.find((lang) => lang.toLowerCase() === locale.toLowerCase());
    const langTag = locale.split(/[_|-]/)[0];
    if (!foundLang) {
        foundLang = mutableArr.find((lang) => lang.toLowerCase() === langTag.toLowerCase());
    }
    // If still not found, fallback to the first locale that is supported for that langTag
    if (!foundLang) {
        foundLang = mutableArr.find((lang) => {
            const langLang = lang.split(/[_|-]/)[0];
            return langLang.toLowerCase() === langTag.toLowerCase();
        });
    }
    return foundLang;
}
/**
 * @param {string} [params.locale] - locale to get lang tag from, otherwise will try to use `navigator.language`
 * @param {LanguageFormat} [params.format=digital] - target to use the lang tag in, defaults to 'digital'
 * @description Use to get the properly formatted language
 * @returns {SupportedLanguage}
 */
export function getLanguage({ locale, format = 'digital', } = {}) {
    const supportedLocale = getLocale({ locale });
    const formattedLocale = format === 'sfPlatform'
        ? supportedLocale.replace('-', '_')
        : supportedLocale.replace('_', '-');
    const supportedLangs = format === 'trailhead'
        ? TRAILHEAD_SUPPORTED_LANGUAGES
        : format === 'sfPlatform'
            ? SF_PLATFORM_SUPPORTED_LANGUAGES
            : DIGITAL_SUPPORTED_LANGUAGES;
    let supportedLang = getSupportedLang({
        locale: formattedLocale,
        supportedLangs,
    });
    if (!supportedLang) {
        supportedLang = format === 'sfPlatform' ? DEFAULT_LOCALE : DEFAULT_LANG;
    }
    return format === 'sfPlatform'
        ? supportedLang
        : supportedLang.replace('_', '-');
}
/**
 * @param {string} locale locale to get the country code from, defaults to `navigator.language`
 * @returns {string} 2-character country code in ISO format
 */
export function getCountryCode(locale) {
    return getLocale({ locale }).split(/[_|-]/)[1];
}
/**
 * @param {string} locale locale to get the region from, defaults to `navigator.language`
 * @returns {string} salesforce.com path segment that identifies the regional website. E.g.`uk` in https://www.salesforce.com/uk
 */
export function getRegion(locale) {
    const globalNavLocale = getLocale({
        locale,
        to: 'globalNav',
    });
    return WWW_SFDC_REGION_BY_LOCALE[globalNavLocale] || '';
}
//# sourceMappingURL=localeFormatting.js.map